import React from "react";
import styled, { css } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Skeleton from "@mui/material/Skeleton";
import { color, radius, shadow, textSize } from "../Theme";

// Shared list pattern for Team (staff, clients, time off) and Timesheets.
// >= 768px: a real table (sticky header, 48px rows, hover state).
// <  768px: stacked cards (no squeezed columns).
// Colours, radius and shadow come from the per-brand tokens only, so GPS Care
// keeps its own theme and TLScare gets its own.

export const TABLE_BREAKPOINT = 768;

// The header sticks just below the dashboard's sticky TopBar (64px tall, z-index 15).
const STICKY_TOP = 64;

const Frame = styled.div`
  width: 100%;
  background: ${color("surface")};
  border: 1px solid ${color("border")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  overflow: clip;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: ${() => textSize("body-sm")}px;
  color: ${color("text")};
`;

const Th = styled.th`
  position: sticky;
  top: ${STICKY_TOP}px;
  z-index: 1;
  text-align: ${(p) => p.$align || "left"};
  padding: 12px 16px;
  font-weight: 700;
  background: ${color("surface")};
  border-bottom: 1px solid ${color("border")};
  color: ${color("text")};
  word-break: break-word;
`;

const Td = styled.td`
  padding: 8px 16px;
  height: 48px;
  vertical-align: middle;
  text-align: ${(p) => p.$align || "left"};
  border-bottom: 1px solid ${color("border")};
  word-break: break-word;
`;

const interactive = css`
  cursor: pointer;
  &:hover > td {
    background: ${color("list")};
  }
  &:focus-visible {
    outline: 2px solid ${color("primary")};
    outline-offset: -2px;
  }
`;

const Tr = styled.tr`
  ${(p) => (p.$interactive ? interactive : "")}
  &:last-child > td {
    border-bottom: none;
  }
  ${(p) =>
    p.$highlight
      ? css`
          & > td:first-child {
            box-shadow: inset 3px 0 0 ${color(p.$highlight)};
          }
        `
      : ""}
`;

const CardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardItem = styled.li`
  background: ${color("surface")};
  border: 1px solid ${color("border")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  color: ${color("text")};
  font-size: ${() => textSize("body-sm")}px;
  ${(p) => (p.$interactive ? "cursor: pointer;" : "")}
  ${(p) =>
    p.$highlight ? css`border-left: 3px solid ${color(p.$highlight)};` : ""}
  &:focus-within, &:hover {
    ${(p) => (p.$interactive ? css`background: ${color("list")};` : "")}
  }
`;

const CardLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  padding: 12px 16px;
  border-radius: ${() => radius("card")}px;
  &:focus-visible {
    outline: 2px solid ${color("primary")};
    outline-offset: -2px;
  }
`;

const CardBody = styled.div`
  padding: 12px 16px;
`;

const Field = styled.div`
  display: flex;
  gap: 8px;
  padding: 2px 0;
  min-width: 0;
`;

const FieldLabel = styled.span`
  flex: 0 0 35%;
  color: ${color("text-muted")};
`;

const FieldValue = styled.span`
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

const PrimaryLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: block;
  &:focus-visible {
    outline: 2px solid ${color("primary")};
    outline-offset: 2px;
    border-radius: ${() => radius("small")}px;
  }
`;

// ---- small building blocks reused by the lists ---------------------------

const AvatarCircle = styled.span`
  flex: 0 0 auto;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${color("primary-light")};
  color: ${color("primary")};
  font-weight: 700;
  font-size: ${(p) => Math.round(p.$size * 0.4)}px;
  overflow: hidden;
`;

export function initialsOf(name) {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Photo when there is one, otherwise initials on the brand's tint.
export function Avatar({ name, url, size = 32 }) {
  if (url) {
    return (
      <AvatarCircle $size={size} aria-hidden="true">
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </AvatarCircle>
    );
  }
  return (
    <AvatarCircle $size={size} aria-hidden="true">
      {initialsOf(name)}
    </AvatarCircle>
  );
}

const ChipEl = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 10px;
  border-radius: ${() => radius("pill")}px;
  background: ${color("primary-light")};
  color: ${color("primary")};
  font-weight: 600;
  font-size: ${() => textSize("body-sm")}px;
  line-height: 18px;
  word-break: break-word;
`;

export function Chip({ children }) {
  return <ChipEl>{children}</ChipEl>;
}

export const Muted = styled.span`
  color: ${color("text-muted")};
`;

const DotEl = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => color(p.$color)};
`;

// Status dot. The accessible name is the existing status label, no new visible text.
export function StatusDot({ color: dotColor, label }) {
  return <DotEl $color={dotColor} role="img" aria-label={label} title={label} />;
}

// ---- skeleton -------------------------------------------------------------

export function TableSkeletonRows({ columns, rows = 3 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={`sk-${r}`} aria-hidden="true">
      {columns.map((c, i) => (
        <Td key={c.key} $align={c.align}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {i === 0 && c.avatar && <Skeleton variant="circular" width={32} height={32} />}
            <Skeleton variant="text" width={i === 0 ? "70%" : "60%"} sx={{ flex: 1 }} />
          </div>
        </Td>
      ))}
    </tr>
  ));
}

export function CardSkeletonList({ rows = 3 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <CardItem key={`sk-${r}`} aria-hidden="true">
      <CardBody>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <div style={{ flex: 1 }}>
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="text" width="80%" />
          </div>
        </div>
      </CardBody>
    </CardItem>
  ));
}

// ---- the table ------------------------------------------------------------

/**
 * columns: [{ key, header, width (e.g. "20%"), align, primary, avatar, render(row, index), mobileHidden }]
 *   `primary` marks the column that carries the row link and is the card title on mobile.
 * rows: array; getRowKey(row, index); getRowHref(row) or onRowClick(row);
 * getRowHighlight(row) -> token name for a left accent (optional);
 * renderCard(row, index) -> custom mobile card content (optional);
 * loading: show skeleton rows (appended below existing rows);
 * empty: node rendered when not loading and there are no rows.
 */
export function DataTable({
  columns,
  rows,
  getRowKey = (_, i) => i,
  getRowHref,
  onRowClick,
  getRowHighlight,
  renderCard,
  loading = false,
  empty = null,
  skeletonRows = 3,
  ariaLabel,
}) {
  const isTable = useMediaQuery(`(min-width:${TABLE_BREAKPOINT}px)`, { noSsr: true });
  const navigate = useNavigate();
  const list = rows ?? [];
  const showEmpty = !loading && list.length < 1 && empty;
  const clickable = Boolean(getRowHref || onRowClick);

  const activate = (row) => {
    if (getRowHref) navigate(getRowHref(row));
    else if (onRowClick) onRowClick(row);
  };

  if (isTable) {
    return (
      <Frame>
          <StyledTable aria-label={ariaLabel} aria-busy={loading || undefined}>
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={c.width ? { width: c.width } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((c) => (
                  <Th key={c.key} scope="col" $align={c.align}>
                    {c.header}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((row, index) => {
                const href = getRowHref?.(row);
                return (
                  <Tr
                    key={getRowKey(row, index)}
                    $interactive={clickable}
                    $highlight={getRowHighlight?.(row)}
                    tabIndex={onRowClick && !getRowHref ? 0 : undefined}
                    onClick={clickable ? () => activate(row) : undefined}
                    onKeyDown={
                      onRowClick && !getRowHref
                        ? (e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              activate(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {columns.map((c) => {
                      const content = c.render ? c.render(row, index) : row[c.key];
                      return (
                        <Td key={c.key} $align={c.align}>
                          {c.primary && href ? (
                            <PrimaryLink to={href} onClick={(e) => e.stopPropagation()}>
                              {content}
                            </PrimaryLink>
                          ) : (
                            content
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
              {loading && <TableSkeletonRows columns={columns} rows={skeletonRows} />}
            </tbody>
          </StyledTable>
        {showEmpty}
      </Frame>
    );
  }

  const defaultCard = (row, index) => (
    <>
      {columns
        .filter((c) => !c.mobileHidden)
        .map((c) => {
          const content = c.render ? c.render(row, index) : row[c.key];
          return c.primary ? (
            <div key={c.key} style={{ fontWeight: 700, marginBottom: 4 }}>
              {content}
            </div>
          ) : (
            <Field key={c.key}>
              <FieldLabel>{c.header}</FieldLabel>
              <FieldValue>{content}</FieldValue>
            </Field>
          );
        })}
    </>
  );

  return (
    <div aria-busy={loading || undefined}>
      <CardList aria-label={ariaLabel}>
        {list.map((row, index) => {
          const href = getRowHref?.(row);
          const body = (renderCard ?? defaultCard)(row, index);
          const highlight = getRowHighlight?.(row);
          return (
            <CardItem
              key={getRowKey(row, index)}
              $interactive={clickable}
              $highlight={highlight}
            >
              {href ? (
                <CardLink to={href}>{body}</CardLink>
              ) : onRowClick ? (
                <CardBody
                  role="button"
                  tabIndex={0}
                  onClick={() => activate(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      activate(row);
                    }
                  }}
                >
                  {body}
                </CardBody>
              ) : (
                <CardBody>{body}</CardBody>
              )}
            </CardItem>
          );
        })}
        {loading && <CardSkeletonList rows={skeletonRows} />}
      </CardList>
      {showEmpty && <Frame>{showEmpty}</Frame>}
    </div>
  );
}

export default DataTable;
